import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col overflow-hidden bg-[#022a51] p-10 text-white lg:flex">
        <img src="/mokora-white.png" alt="Mokora" className="relative z-10 h-10 w-auto self-start" />
        <div className="relative z-10 flex flex-1 flex-col justify-center">
          <h1 className="font-heading text-5xl font-extrabold leading-none tracking-tight">Timesheet Tool</h1>
          <div className="mt-6 h-1.5 w-16 rounded-full bg-[hsl(var(--accent))]" />
        </div>
        <div className="relative z-10 font-mono text-xs text-slate-400/70">© 2026 Mokora</div>
        <img src="/mokora-icon.png" alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 opacity-[0.06]" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 lg:hidden">
            <img src="/mokora-navy.png" alt="Mokora" className="h-9 w-auto dark:hidden" />
            <img src="/mokora-white.png" alt="Mokora" className="hidden h-9 w-auto dark:block" />
          </div>

          <h2 className="font-heading text-2xl font-extrabold tracking-tight">Set a new password</h2>
          <p className="mt-1 text-sm text-muted-foreground">Choose a new password for your account.</p>

          {!token && (
            <div data-testid="reset-missing-token" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              This reset link is missing its token. Please request a new link.
            </div>
          )}

          {error && (
            <div data-testid="reset-error" className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          <form data-testid="reset-form" onSubmit={submit} className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="r-password">New password</Label>
              <PasswordInput id="r-password" data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-confirm">Confirm password</Label>
              <PasswordInput id="r-confirm" data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" />
            </div>
            <Button data-testid="reset-submit-btn" type="submit" disabled={loading || !token} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reset password
            </Button>
          </form>

          <Link to="/login" data-testid="back-to-login-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#022a51] hover:underline dark:text-sky-300">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
